import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Report, ReportStatus } from "../../types/Report";
import ReportViewTable from "./ReportViewTable";
import axios from "axios";
import CachedIcon from "@mui/icons-material/Cached";
import { Card, CircularProgress, Stack, Typography } from "@mui/material";
import ReportStatusIndicator from "../../components/ReportStatusIndicator";
import NotStartedIcon from "@mui/icons-material/NotStarted";
import DiscFullIcon from "@mui/icons-material/DiscFull";
import { useSnackbar } from "../../providers/SnackbarContext";

interface ReportViewDialogProps {
  open: boolean;
  report: Report;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  canCreateNewReport: boolean;
}

export default function ReportViewDialog({
  open,
  report,
  setOpen,
  canCreateNewReport,
}: ReportViewDialogProps) {
  const [currentReport, setCurrentReport] = React.useState<Report>();
  const [resetting, setResetting] = React.useState(false);
  const { openSnackbar } = useSnackbar();

  const handleClose = () => {
    setOpen(false);
  };

  const fetchReport = () => {
    axios
      .get(`${process.env.REACT_APP_API_HOST}/report?reportId=${report._id}`)
      .then((response) => {
        setCurrentReport(response.data);
      });
  };

  const restartReport = (withErrors: boolean) => {
    setResetting(true);
    axios
      .post(`${process.env.REACT_APP_API_HOST}/admin/restart-app`)
      .then((restartResponse) => {
        if (restartResponse.data.success) {
          setTimeout(() => {
            let exludedCuits: number[] = [];
            if (withErrors) {
              // api de reporte con errores
              exludedCuits =
                report.accountReports
                  ?.filter((ar) => !ar.error)
                  .reduce(
                    (cuitList: number[], curr) => [...cuitList, curr.cuit],
                    [],
                  ) || [];
            } else {
              // api de reporte sin errores
              exludedCuits =
                report.accountReports.reduce(
                  (cuitList: number[], curr) => [...cuitList, curr.cuit],
                  [],
                ) || [];
            }

            // Re-ejecutar reporte
            axios
              .post(`${process.env.REACT_APP_API_HOST}/report/process`, {
                dateFrom: report.reportPeriod.split("/")[0] + "T00:00",
                dateUntil: report.reportPeriod.split("/")[1] + "T00:00",
                type: report.type,
                excludeCuits: exludedCuits,
                reportId: report._id,
              })
              .then(() => {
                openSnackbar(
                  "El reporte se ha reiniciado, y se procesará en los siguientes minutos",
                  "success",
                );
                setTimeout(() => {
                  fetchReport();
                  setResetting(false);
                }, 1000);
              })
              .catch(() => {
                openSnackbar("Hubo un error al reiniciar el reporte", "error");
                setResetting(false);
              });
          }, 5000);
        } else {
          throw new Error("Error reiniciando la aplicación");
        }
      })
      .catch(() => {
        openSnackbar("Error reiniciando el reporte.", "error");
        setResetting(false);
      });
  };

  React.useEffect(() => {
    if (open) {
      fetchReport();

      const refreshInterval = setInterval(fetchReport, 10000);

      return () => clearInterval(refreshInterval);
    } else {
      setCurrentReport(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, open]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              // minWidth: "1000px",
              minWidth: "95%",
            },
          },
        }}
      >
        <DialogTitle>
          Datos del reporte - {report.reportPeriod} - {report.type}
        </DialogTitle>
        <DialogContent>
          {!currentReport || resetting ? (
            <Stack justifyContent="center" alignItems="center" gap={2} p={5}>
              <CircularProgress />
              <Typography maxWidth={300} textAlign="center">
                Cargando información del reporte. Esto puede demorar unos
                minutos...
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack direction="row" alignItems="center" gap={2} mb={2}>
                {currentReport.status !== ReportStatus.READY && (
                  <Button
                    variant="contained"
                    endIcon={<CachedIcon />}
                    sx={{ mb: 2 }}
                    onClick={fetchReport}
                    disabled={resetting}
                  >
                    Refrescar
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  endIcon={<NotStartedIcon />}
                  sx={{ mb: 2 }}
                  onClick={() => restartReport(false)}
                  disabled={resetting || !canCreateNewReport}
                >
                  Reiniciar proceso
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  endIcon={<DiscFullIcon />}
                  sx={{ mb: 2 }}
                  onClick={() => restartReport(true)}
                  disabled={resetting || !canCreateNewReport}
                >
                  Reiniciar proceso con errores
                </Button>
              </Stack>
              <Card variant="outlined" sx={{ my: 2, p: 2 }}>
                <ReportStatusIndicator report={currentReport} />
              </Card>
              <ReportViewTable
                report={currentReport}
                handleRefresh={fetchReport}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
